import React, { useState, useEffect } from 'react';
import { Edit2, Trash2, Plus, Search, Filter, Download, RefreshCw, Check, X, ChevronLeft, ChevronRight } from 'lucide-react';
import './SuperAdministator.css';
import { CustomSelect } from './CustomSelect.js';
import './CustomSelect.css';

const ROLES = ['System Administrator', 'Broadcast', 'AskHR Analytics', 'Case Management', 'Pinned Users Report'];
const STATUSES = ['Active', 'Inactive'];
const ITEMS_PER_PAGE = 6;

export default function Super_Administator() {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [editingId, setEditingId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);

  // Backend data states
  const [liveCountries, setLiveCountries] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState(null);

  // Edit state - only status and endDate are editable
  const [editFormData, setEditFormData] = useState({
    status: '',
    endDate: ''
  });

  // Form states for Add User Modal
  const [formData, setFormData] = useState({
    role: '',
    email: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    status: 'Active',
    country: ''
  });

  // Fetch role assignments on component mount
  useEffect(() => {
    fetchRoleAssignments();
    fetchLiveCountries();
  }, []);

  // Fetch group members when modal opens
  useEffect(() => {
    if (showAddModal) {
      fetchGroupMembers();
    }
  }, [showAddModal]);

  // ============================================
  // CALCULATE STATUS BASED ON DATE RANGE
  // ============================================
  const calculateStatus = (startDate, endDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);

    // If no end date, check if start date has passed
    if (!endDate) {
      const status = start <= today ? 'Active' : 'Inactive';
      console.log(`📅 Calculate Status (No End Date): Start=${startDate}, Today=${today.toISOString()}, Result=${status}`);
      return status;
    }

    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    // Check if today is within the date range
    let status;
    if (today >= start && today <= end) {
      status = 'Active';
    } else {
      status = 'Inactive';
    }

    console.log(`📅 Calculate Status: Start=${startDate}, End=${endDate}, Today=${today.toISOString().split('T')[0]}, Result=${status}`);
    return status;
  };

  // ============================================
  // CHECK IF DATE HAS EXPIRED
  // ============================================
  const isDateExpired = (endDate) => {
    if (!endDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    return today > end;
  };

  // ============================================
  // UPDATE STATUS IN DATABASE
  // ============================================
  const updateStatusInDatabase = async (role, newStatus) => {
    console.log(`🔄 Attempting to update status for ${role.email} from "${role.status}" to "${newStatus}"`);

    try {
      const payload = {
        role: role.role,
        email: role.email,
        startDate: role.startDate,
        endDate: role.endDate,
        status: newStatus,
        country: role.country
      };

      console.log('📤 Sending PUT request:', payload);
      console.log('📤 URL:', `/api/role-assignments/${role.id}?email=${encodeURIComponent(role.email)}`);

      const response = await fetch(`/api/role-assignments/${role.id}?email=${encodeURIComponent(role.email)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      console.log('📥 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response error:', errorText);
        throw new Error(`Failed to update status: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Status auto-updated successfully:', result);
      return true;
    } catch (err) {
      console.error('❌ Error auto-updating status:', err);
      return false;
    }
  };

  // ============================================
  // FETCH ROLE ASSIGNMENTS FROM COSMOS
  // ============================================
  const fetchRoleAssignments = async () => {
    setLoadingUsers(true);
    setError(null);

    try {
      const response = await fetch('/api/role-assignments', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch role assignments');
      }

      const data = await response.json();

      const transformedUsers = [];
      let updateCount = 0;

      for (const role of data) {
        // ✅ KEY FIX: Only auto-update to Inactive (never force back to Active)
        // If DB says Inactive, ALWAYS respect it — don't recalculate over it
        let resolvedStatus = role.status;

        // Only override if DB says Active but the date has actually expired
        if (role.status === 'Active' && isDateExpired(role.endDate)) {
          resolvedStatus = 'Inactive';
          const updateSuccess = await updateStatusInDatabase(role, 'Inactive');
          if (updateSuccess) updateCount++;
        }

        // ❌ REMOVED: the old logic that forced Active based on date range
        // That was overwriting your manual Inactive settings

        transformedUsers.push({
          id: role.id,
          role: role.role,
          email: role.email,
          startDate: role.startDate,
          endDate: role.endDate,
          status: resolvedStatus, // ✅ Uses DB value unless date expired
          countries: [role.country]
        });
      }

      if (updateCount > 0) {
        console.log(`✅ Auto-deactivated ${updateCount} expired role assignment(s)`);
      }

      setUsers(transformedUsers.sort((a, b) => a.role.localeCompare(b.role)));
    } catch (err) {
      console.error('❌ Error fetching role assignments:', err);
      setError('Failed to load role assignments');
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchLiveCountries = async () => {
    setLoadingCountries(true);
    setError(null);
    try {
      const response = await fetch('/api/golive-countries', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch countries');
      }

      const data = await response.json();

      if (data.success) {
        setLiveCountries(['Global', ...data.countries]);
        console.log('✅ Live countries fetched:', data.countries);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('❌ Error fetching countries:', err);
      setError('Failed to load countries');
      setLiveCountries(['Global', 'Philippines', 'Indonesia', 'USA', 'Canada', 'UK']);
    } finally {
      setLoadingCountries(false);
    }
  };

  const fetchGroupMembers = async () => {
    setLoadingMembers(true);
    setError(null);
    try {
      const response = await fetch('/SSO_Cred', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch group members');
      }

      const data = await response.json();

      if (data.data && data.data.groupMembers) {
        const members = data.data.groupMembers.map(member => ({
          email: member.mail || member.userPrincipalName,
          displayName: member.displayName,
          id: member.id
        })).filter(member => member.email);
        console.log('✅ All users fetched:', members.length);
        setGroupMembers(members.sort((a, b) => a.displayName.localeCompare(b.displayName))); // ✅ CHANGED
      } else if (data.data && data.data.allUsers) {
        const members = data.data.allUsers.map(user => ({
          email: user.mail || user.userPrincipalName,
          displayName: user.displayName,
          id: user.id
        })).filter(member => member.email);
        console.log('✅ All users fetched:', members.length);
        setGroupMembers(members.sort((a, b) => a.displayName.localeCompare(b.displayName))); // ✅ CHANGED
      }
      else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('❌ Error fetching group members:', err);
      setError('Failed to load group members');
      setGroupMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.role.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'All' || user.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus]);

  const toggleSelectUser = (id) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedUsers(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === currentUsers.length && currentUsers.length > 0) {
      const newSelected = new Set(selectedUsers);
      currentUsers.forEach(user => newSelected.delete(user.id));
      setSelectedUsers(newSelected);
    } else {
      const newSelected = new Set(selectedUsers);
      currentUsers.forEach(user => newSelected.add(user.id));
      setSelectedUsers(newSelected);
    }
  };

  const isAllCurrentPageSelected = () => {
    return currentUsers.length > 0 && currentUsers.every(user => selectedUsers.has(user.id));
  };

  // ============================================
  // EDIT FUNCTIONALITY
  // ============================================
  const handleEditClick = (user) => {
    console.log('✏️ Edit clicked for:', user.email, 'Current status:', user.status);
    setEditingId(user.id);
    setEditFormData({
      status: user.status,
      endDate: user.endDate || ''
    });
  };

  const handleCancelEdit = () => {
    console.log('❌ Edit cancelled');
    setEditingId(null);
    setEditFormData({
      status: '',
      endDate: ''
    });
  };

  const handleSaveEdit = async (user) => {
    console.log('💾 Save clicked - Attempting to save:', editFormData);

    // Validation: Check if trying to set Active when date has expired
    if (editFormData.status === 'Active' && isDateExpired(editFormData.endDate)) {
      alert('Cannot set status to Active: End date has passed');
      console.log('⚠️ Blocked: Cannot set Active - date expired');
      return;
    }

    // Validation: Check start date is before end date
    if (user.startDate && editFormData.endDate) {
      const start = new Date(user.startDate);
      const end = new Date(editFormData.endDate);
      if (start > end) {
        alert('End date cannot be before start date');
        return;
      }
    }

    try {
      const payload = {
        role: user.role,
        email: user.email,
        startDate: user.startDate, // Keep original start date
        endDate: editFormData.endDate || null,
        status: editFormData.status,
        country: user.countries[0]
      };

      console.log('📤 Sending manual update:', payload);
      console.log('📤 URL:', `/api/role-assignments/${user.id}?email=${encodeURIComponent(user.email)}`);

      const response = await fetch(`/api/role-assignments/${user.id}?email=${encodeURIComponent(user.email)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      console.log('📥 Manual update response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response error:', errorText);
        throw new Error(`Failed to update: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Manual update successful:', result);

      // Refresh the data
      await fetchRoleAssignments();

      // Reset edit state
      setEditingId(null);
      setEditFormData({ status: '', endDate: '' });

    } catch (err) {
      console.error('❌ Error in manual update:', err);
      alert('Failed to update: ' + err.message);
    }
  };

  // ============================================
  // DELETE ROLE ASSIGNMENT
  // ============================================
  const handleDelete = async (id, email) => {
    if (!window.confirm('Are you sure you want to delete this role assignment?')) {
      return;
    }

    try {
      const response = await fetch(`/api/role-assignments/${id}?email=${encodeURIComponent(email)}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to delete role assignment');
      }

      await fetchRoleAssignments();
      selectedUsers.delete(id);
      setSelectedUsers(new Set(selectedUsers));

      console.log('✅ Role assignment deleted');
    } catch (err) {
      console.error('❌ Error deleting role assignment:', err);
      alert('Failed to delete role assignment: ' + err.message);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedUsers.size} selected users?`)) {
      return;
    }

    try {
      const deletePromises = Array.from(selectedUsers).map(id => {
        const user = users.find(u => u.id === id);
        if (user) {
          return fetch(`/api/role-assignments/${id}?email=${encodeURIComponent(user.email)}`, {
            method: 'DELETE',
            credentials: 'include'
          });
        }
        return Promise.resolve();
      });

      await Promise.all(deletePromises);
      await fetchRoleAssignments();
      setSelectedUsers(new Set());

      console.log('✅ Bulk delete completed');
    } catch (err) {
      console.error('❌ Error bulk deleting:', err);
      alert('Failed to delete some role assignments');
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // ============================================
  // SUBMIT NEW USER (CREATE IN COSMOS)
  // ============================================
  const handleSubmitUser = async () => {
    // Validation
    if (!formData.role || !formData.email || !formData.startDate || !formData.country) {
      alert('Please fill in all required fields (Role, Email, Start Date, and Country)');
      return;
    }

    try {
      const response = await fetch('/api/role-assignments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          role: formData.role,
          email: formData.email,
          startDate: formData.startDate,
          endDate: formData.endDate || null,
          status: formData.status,
          country: formData.country
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create role assignment');
      }

      const result = await response.json();
      console.log('✅ Role assignment created:', result);

      await fetchRoleAssignments();

      setFormData({
        role: '',
        email: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        status: 'Active',
        country: ''
      });
      setShowAddModal(false);

      alert('Role assignment created successfully!');
    } catch (err) {
      console.error('❌ Error creating role assignment:', err);
      alert('Failed to create role assignment: ' + err.message);
    }
  };

  const handleEmailSelect = (e) => {
    const selectedEmail = e.target.value;
    if (selectedEmail) {
      setFormData(prev => ({
        ...prev,
        email: selectedEmail
      }));
    }
  };

  // ============================================
  // DOWNLOAD — calls backend endpoint
  // ============================================
  const handleDownload = () => {
    window.location.href = '/api/role-assignments/download';
  };

  // Build option arrays for CustomSelect
  const roleOptions = ROLES.map(r => ({ value: r, label: r }));
  const statusOptions = STATUSES.map(s => ({ value: s, label: s }));
  const statusFilterOptions = [{ value: 'All', label: 'All Status' }, ...STATUSES.map(s => ({ value: s, label: s }))];
  const emailOptions = groupMembers.map(m => ({ value: m.email, label: `${m.displayName} (${m.email})` }));
  const countryOptions = liveCountries.map(c => ({ value: c, label: c }));

  // Inline edit status options (disable Active if date expired)
  const editStatusOptions = STATUSES.map(s => ({
    value: s,
    label: s === 'Active' && isDateExpired(editFormData.endDate) ? 'Active (Date Expired)' : s,
    disabled: s === 'Active' && isDateExpired(editFormData.endDate)
  }));

  return (
    <div className="uma-wrapper">
      <div className="uma-container">
        <div className="uma-page-header">
          <h1 className="uma-page-title">Role Assignment</h1>
          <p className="uma-page-subtitle">Manage user roles, permissions, and access control</p>
        </div>

        <div className="uma-action-bar-card">
          <div className="uma-action-bar-content">
            <div className="uma-search-wrapper">
              <div className="uma-search-input-wrapper">
                <Search className="uma-search-icon" size={20} />
                <input
                  type="text"
                  placeholder="Search by email or role..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="uma-search-input"
                />
              </div>
            </div>

            <div className="uma-action-buttons-wrapper">
              {/* Status filter — CustomSelect */}
              <div style={{ minWidth: '150px' }}>
                <CustomSelect
                  value={filterStatus}
                  onChange={setFilterStatus}
                  options={statusFilterOptions}
                  placeholder="All Status"
                />
              </div>

              <button
                onClick={() => setShowAddModal(true)}
                className="uma-btn-add-user"
              >
                <Plus size={20} />
                Add User
              </button>

              {/* {selectedUsers.size > 0 && (
                <button
                  onClick={handleBulkDelete}
                  className="uma-btn-bulk-delete"
                >
                  <Trash2 size={20} />
                  Delete ({selectedUsers.size})
                </button>
              )} */}

              <button
                className="uma-btn-icon-action"
                onClick={async () => {
                  const response = await fetch('/api/roles/download', {
                    credentials: 'include'
                  });
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `Role_Assignments_${new Date().toISOString().split('T')[0]}.xlsx`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                }}
              >
                <Download size={20} />
              </button>

              <button
                className="uma-btn-icon-action"
                onClick={() => {
                  console.log('🔄 Manual refresh triggered');
                  fetchRoleAssignments();
                  fetchLiveCountries();
                  if (showAddModal) fetchGroupMembers();
                }}
              >
                <RefreshCw size={20} />
              </button>
            </div>
          </div>
        </div>

        {loadingUsers && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
            <p>Loading role assignments...</p>
          </div>
        )}

        {!loadingUsers && (
          <div className="uma-table-card">
            <div className="uma-table-wrapper">
              <table className="uma-users-table">
                <thead>
                  <tr className="uma-table-header-row">
                    {/* <th className="uma-table-header uma-col-checkbox">
                      <input
                        type="checkbox"
                        checked={isAllCurrentPageSelected()}
                        onChange={toggleSelectAll}
                        className="uma-checkbox-input"
                      />
                    </th> */}
                    <th className="uma-table-header uma-col-role">ROLE</th>
                    <th className="uma-table-header uma-col-email">EMAIL</th>
                    <th className="uma-table-header uma-col-start-date">START DATE</th>
                    <th className="uma-table-header uma-col-end-date">END DATE</th>
                    <th className="uma-table-header uma-col-status">STATUS</th>
                    <th className="uma-table-header uma-col-country">COUNTRY</th>
                    <th className="uma-table-header uma-col-actions">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="uma-table-body">
                  {currentUsers.map((user) => {
                    const dateExpired = isDateExpired(editingId === user.id ? editFormData.endDate : user.endDate);

                    return (
                      <tr
                        key={user.id}
                        className="uma-table-row"
                      >
                        {/* <td className="uma-table-cell uma-col-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedUsers.has(user.id)}
                            onChange={() => toggleSelectUser(user.id)}
                            className="uma-checkbox-input"
                          />
                        </td> */}
                        <td className="uma-table-cell uma-col-role uma-cell-role">
                          {user.role}
                        </td>
                        <td className="uma-table-cell uma-col-email uma-cell-email">
                          {user.email}
                        </td>
                        <td className="uma-table-cell uma-col-start-date uma-cell-start-date">
                          {new Date(user.startDate).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: '2-digit'
                          })}
                        </td>
                        <td className="uma-table-cell uma-col-end-date uma-cell-end-date">
                          {editingId === user.id ? (
                            <input
                              type="date"
                              value={editFormData.endDate}
                              min={user.startDate || new Date().toISOString().split('T')[0]}
                              onChange={(e) => {
                                console.log('End date changed to:', e.target.value);
                                setEditFormData(prev => ({ ...prev, endDate: e.target.value }));
                              }}
                              className="uma-date-input"
                            />
                          ) : (
                            user.endDate ? new Date(user.endDate).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: '2-digit'
                            }) : '-'
                          )}
                        </td>
                        <td className="uma-table-cell uma-col-status">
                          {editingId === user.id ? (
                            <CustomSelect
                              value={editFormData.status}
                              onChange={(val) => {
                                console.log('Status dropdown changed to:', val);
                                setEditFormData(prev => ({ ...prev, status: val }));
                              }}
                              options={editStatusOptions}
                              className="csel-inline"
                            />
                          ) : (
                            <span className={`uma-status-badge uma-status-${user.status.toLowerCase()}`}>
                              {user.status === 'Active' ? <Check size={14} /> : <X size={14} />}
                              {user.status}
                            </span>
                          )}
                        </td>
                        <td className="uma-table-cell uma-col-country">
                          <div className="uma-countries-wrapper">
                            {user.countries.map((country, idx) => (
                              <span key={idx} className="uma-country-badge">
                                {country}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="uma-table-cell uma-col-actions uma-cell-actions">
                          <div className="uma-action-buttons">
                            {editingId === user.id ? (
                              <>
                                <button
                                  onClick={() => handleSaveEdit(user)}
                                  className="uma-btn-save"
                                  title="Save"
                                >
                                  <Check size={18} />
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="uma-btn-cancel"
                                  title="Cancel"
                                >
                                  <X size={18} />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleEditClick(user)}
                                  className="uma-btn-edit"
                                  title="Edit"
                                >
                                  <Edit2 size={18} />
                                </button>
                                {/* <button
                                  // onClick={() => handleDelete(user.id, user.email)}
                                  className="uma-btn-delete"
                                  title="Delete"
                                >
                                  <Trash2 size={18} />
                                </button> */}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredUsers.length === 0 && !loadingUsers && (
              <div className="uma-empty-state">
                <div className="uma-empty-state-icon">
                  <Search size={64} />
                </div>
                <h3 className="uma-empty-state-title">No users found</h3>
                <p className="uma-empty-state-text">Try adjusting your search or filters</p>
              </div>
            )}

            {filteredUsers.length > 0 && (
              <div className="uma-pagination-wrapper">
                <div className="uma-pagination-info">
                  Showing <span className="uma-pagination-count">{startIndex + 1}</span> to{' '}
                  <span className="uma-pagination-count">{Math.min(endIndex, filteredUsers.length)}</span> of{' '}
                  <span className="uma-pagination-count">{filteredUsers.length}</span> role assignments
                </div>
                <div className="uma-pagination-controls">
                  <button
                    className="uma-btn-pagination uma-btn-pagination-nav"
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft size={18} />
                    Previous
                  </button>

                  <div className="uma-pagination-numbers">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        className={`uma-btn-pagination-number ${currentPage === pageNum ? 'uma-btn-pagination-number-active' : ''
                          }`}
                      >
                        {pageNum}
                      </button>
                    ))}
                  </div>

                  <button
                    className="uma-btn-pagination uma-btn-pagination-nav"
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Add User Modal */}
        {showAddModal && (
          <div className="uma-modal-overlay">
            <div className="uma-modal-content">
              <div className="uma-modal-header">
                <h2 className="uma-modal-title">Add New User</h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setFormData({
                      role: '',
                      email: '',
                      startDate: new Date().toISOString().split('T')[0],
                      endDate: '',
                      status: 'Active',
                      country: ''
                    });
                  }}
                  className="uma-modal-close"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="uma-modal-body">
                {error && (
                  <div className="uma-error-message">
                    {error}
                  </div>
                )}

                <div className="uma-form-group">
                  <label className="uma-form-label">Role *</label>
                  <CustomSelect
                    value={formData.role}
                    onChange={(val) => handleFormChange('role', val)}
                    options={roleOptions}
                    placeholder="Select role..."
                  />
                </div>

                <div className="uma-form-group">
                  <label className="uma-form-label">
                    Email *
                    {loadingMembers && <span className="uma-loading-text"> (Loading...)</span>}
                  </label>
                  <CustomSelect
                    value={formData.email}
                    onChange={(val) => handleFormChange('email', val)}
                    options={emailOptions}
                    placeholder={loadingMembers ? 'Loading users...' : 'Select user from group...'}
                    disabled={loadingMembers}
                    searchable={true}
                  />
                  <p className="uma-form-hint">
                    Select users
                  </p>
                </div>

                <div className="uma-form-row">
                  <div className="uma-form-group">
                    <label className="uma-form-label">Start Date *</label>
                    <input
                      type="date"
                      className="uma-form-input"
                      value={formData.startDate}
                      min={new Date().toISOString().split('T')[0]}
                      onChange={(e) => handleFormChange('startDate', e.target.value)}
                    />
                    <p className="uma-form-hint">Defaults to today</p>
                  </div>
                  <div className="uma-form-group">
                    <label className="uma-form-label">End Date (Optional)</label>
                    <input
                      type="date"
                      className="uma-form-input"
                      value={formData.endDate}
                      min={formData.startDate || new Date().toISOString().split('T')[0]}
                      onChange={(e) => handleFormChange('endDate', e.target.value)}
                    />
                  </div>
                </div>

                <div className="uma-form-group">
                  <label className="uma-form-label">Status</label>
                  <CustomSelect
                    value={formData.status}
                    onChange={(val) => handleFormChange('status', val)}
                    options={statusOptions}
                    placeholder="Select status..."
                  />
                </div>

                <div className="uma-form-group">
                  <label className="uma-form-label">
                    Country *
                    {loadingCountries && <span className="uma-loading-text"> (Loading...)</span>}
                  </label>
                  <CustomSelect
                    value={formData.country}
                    onChange={(val) => handleFormChange('country', val)}
                    options={countryOptions}
                    placeholder={loadingCountries ? 'Loading countries...' : 'Select country...'}
                    disabled={loadingCountries}
                    searchable={true}
                  />
                  <p className="uma-form-hint">Select one country for this role assignment</p>
                </div>
              </div>

              <div className="uma-modal-footer">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setFormData({
                      role: '',
                      email: '',
                      startDate: new Date().toISOString().split('T')[0],
                      endDate: '',
                      status: 'Active',
                      country: ''
                    });
                  }}
                  className="uma-btn-modal-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitUser}
                  className="uma-btn-modal-submit"
                >
                  Add User
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}